/**
 * HospitalDetailPage — one hospital's queue trend + rough estimated wait.
 *
 * Mirrors the overview's split:
 *   - `DetailView` — pure, prop-driven; renders the header, the trend chart
 *     panel and the estimated-wait panel from injected query-result props, so
 *     every state is unit-testable without a network or a router.
 *   - `HospitalDetailPage` — the connected default export; reads `:slug` from
 *     the router, owns the selected time-zoom, and wires the generated TanStack
 *     Query hooks.
 *
 * The trend chart's zoom spans two endpoints: short steps (≤48h) poll the
 * 5-minute `/history`; long steps (7g+) read the hourly-aggregate `/trend`,
 * which is historical and needs no polling. Only the active query is enabled,
 * so switching zoom swaps data sources without double-fetching.
 */

import { useCallback, useEffect, useState, type MouseEvent } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  useGetHospitalHistory,
  useGetHospitalTrend,
  useGetEstimatedWait,
  useGetHospitalHeatmap,
  useGetLiveOverview,
} from "@/api/generated/hospitals/hospitals";
import { useGetCollectorStatus } from "@/api/generated/status/status";
import type {
  CollectorStatusResponse,
  EstimatedWaitResponse,
  HospitalHeatmapResponse,
  HospitalQueueState,
} from "@/api/generated/model";
import { HistoryChartCard } from "@/components/HistoryChartCard";
import {
  buildChartSeries,
  buildTrendSeries,
  ZOOM_PARAM,
  zoomFromToken,
  zoomToToken,
  type ChartSeries,
  type TimeZoom,
} from "@/lib/history";
import { EstimatedWaitPanel } from "@/components/EstimatedWaitPanel";
import { HeatmapPanel } from "@/components/HeatmapPanel";
import { DetailHero } from "@/components/DetailHero";
import { DetailTriageBreakdown } from "@/components/DetailTriageBreakdown";
import { StaleDataBanner } from "@/components/StaleDataBanner";
import { congestionLevelFromCount } from "@/lib/congestion";
import { currentWaiting, detailMeasuredAt } from "@/lib/detail";
import { getMorphSeed, useMorphNavigate } from "@/lib/morph";
import { collectorSummary } from "@/lib/status";

/** How often to re-poll the live detail data (the feed refreshes every ~5 min). */
const REFETCH_INTERVAL_MS = 60_000;

interface QueryResult<T> {
  data: T | undefined;
  isPending: boolean;
  isError: boolean;
  refetch: () => void;
}

/** The trend chart's data for the active zoom: already-built series plus states. */
interface ChartState {
  series: ChartSeries[];
  isPending: boolean;
  isError: boolean;
  refetch: () => void;
}

interface DetailViewProps {
  slug: string;
  chart: ChartState;
  wait: QueryResult<EstimatedWaitResponse>;
  heatmap: QueryResult<HospitalHeatmapResponse>;
  zoom: TimeZoom;
  onZoomChange: (zoom: TimeZoom) => void;
  /**
   * This hospital's live queue state from the `/hospitals` overview feed, or
   * `undefined` until it arrives. It carries the exact per-triage integers the
   * trend/wait responses can't (see DetailTriageBreakdown), and — when it has a
   * reading — is the authoritative source for the hero total/instant too, so the
   * hero number and the breakdown never disagree.
   */
  live?: HospitalQueueState | undefined;
  /** Whether the live overview query is still pending (no cached data yet). */
  liveIsPending?: boolean;
  /** Collector health, for the stale-data banner; `undefined` while it loads. */
  collectorStatus?: CollectorStatusResponse | undefined;
  /** Injectable clock for deterministic tests. */
  now?: Date | undefined;
}

export function DetailView({
  slug,
  chart,
  wait,
  heatmap,
  zoom,
  onZoomChange,
  live,
  liveIsPending = false,
  collectorStatus,
  now,
}: DetailViewProps) {
  // When arrived via the shared-element morph, the source card handed us the
  // values it was showing so the hero renders real content (number, name) at
  // snapshot time — the big number then morphs continuously instead of fading
  // in from an empty state while this page's own queries load. Frozen at mount;
  // a plain deep-link/reload has no seed (getMorphSeed() is null).
  const [seed] = useState(() => getMorphSeed());
  const morph = useMorphNavigate();

  // Name comes back on the wait response (always polled, both zoom modes) or the
  // morph seed.
  const hospital = wait.data?.hospital;
  const nameIt = hospital?.nameIt ?? seed?.nameIt;
  const nameDe = hospital?.nameDe ?? seed?.nameDe;
  // The single most important fact — people waiting here, now. When the live
  // overview reading has arrived it is the exact, authoritative source (so the
  // hero total equals the breakdown's Σ); otherwise fall back to the value
  // reconstructed from the wait response (lib/detail.ts), then the seed.
  const liveReading = live?.hasData ? live : undefined;
  const total =
    liveReading?.totalWaiting ?? currentWaiting(wait.data, undefined) ?? seed?.total ?? null;
  const level = congestionLevelFromCount(total);
  const measuredAt =
    liveReading?.measuredAt ?? detailMeasuredAt(wait.data, undefined) ?? seed?.measuredAt ?? null;

  const handleBack = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (!morph.enabled) return;
    e.preventDefault();
    morph.toOverview(slug);
  };

  // Collector health → the non-blocking stale banner above the hero. Undefined
  // (status still loading, or its own query errored) ⇒ `unknown` ⇒ hidden.
  const collector = collectorStatus ? collectorSummary(collectorStatus) : undefined;

  return (
    <main className="detail">
      {/* ── Stale-data banner (non-blocking; live data still renders below) ── */}
      <StaleDataBanner
        level={collector?.level ?? "unknown"}
        ageLabel={collector?.ageLabel ?? null}
      />

      <nav className="detail__back" aria-label="Navigazione">
        <Link to="/" className="detail__back-link" onClick={handleBack}>
          ← Tutti i Pronto Soccorso
        </Link>
      </nav>

      <DetailHero
        slug={slug}
        nameIt={nameIt}
        nameDe={nameDe}
        total={total}
        level={level}
        measuredAt={measuredAt}
        now={now}
      />

      <DetailTriageBreakdown state={live} isPending={liveIsPending} />

      <div className="detail__panels">
        <HistoryChartCard
          series={chart.series}
          isPending={chart.isPending}
          isError={chart.isError}
          onRetry={chart.refetch}
          zoom={zoom}
          onZoomChange={onZoomChange}
        />
        <EstimatedWaitPanel
          data={wait.data}
          isPending={wait.isPending}
          isError={wait.isError}
          onRetry={wait.refetch}
        />
        <HeatmapPanel heatmap={heatmap} />
      </div>
    </main>
  );
}

export default function HospitalDetailPage() {
  const { slug = "" } = useParams<{ slug: string }>();

  // The time-zoom lives on the URL (`?finestra=<label>`), so it's deep-linkable
  // and shareable. A cold load with a valid token opens at that zoom; a missing
  // or unrecognised token falls back to DEFAULT_ZOOM (never a crash/blank).
  const [searchParams, setSearchParams] = useSearchParams();
  const rawToken = searchParams.get(ZOOM_PARAM);
  const zoom = zoomFromToken(rawToken);
  const isTrend = zoom.kind === "trend";

  // Changing zoom rewrites the URL in place (`replace`) — bookmarkable without
  // spamming the Back stack, so overview→detail→Back still returns to the
  // overview regardless of how many times the user zoomed. Preserves any other
  // params (forward-compat with a future `?istante=`, #94).
  const onZoomChange = useCallback(
    (next: TimeZoom) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set(ZOOM_PARAM, zoomToToken(next));
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Keep a shared/bookmarked link honest: when `?finestra=` is present but not in
  // its canonical form — a bogus token (`nonsense` → resolves to the default) or
  // a valid-but-messy one (`tutto`/` 30G ` → `Tutto`/`30g`) — rewrite it in place
  // to the resolved token, so the URL always reads as what's actually shown.
  // Only the finestra param is touched (a reserved `?istante=` is preserved).
  const canonicalToken = zoomToToken(zoom);
  useEffect(() => {
    if (rawToken !== null && rawToken !== canonicalToken) {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set(ZOOM_PARAM, canonicalToken);
          return params;
        },
        { replace: true },
      );
    }
  }, [rawToken, canonicalToken, setSearchParams]);

  // Short zoom: the 5-minute history, polled live. Long zoom: the hourly-aggregate
  // trend — historical, so no polling. Only the active query is enabled; the idle
  // one holds its cache and doesn't fetch.
  const historyQuery = useGetHospitalHistory(
    slug,
    { hours: isTrend ? 24 : zoom.hours },
    { query: { enabled: !isTrend, refetchInterval: REFETCH_INTERVAL_MS } },
  );
  const trendQuery = useGetHospitalTrend(
    slug,
    { window: isTrend ? zoom.window : "7d" },
    { query: { enabled: isTrend } },
  );

  const chart: ChartState = isTrend
    ? {
        series: trendQuery.data ? buildTrendSeries(trendQuery.data) : [],
        isPending: trendQuery.isPending,
        isError: trendQuery.isError,
        refetch: () => void trendQuery.refetch(),
      }
    : {
        series: historyQuery.data ? buildChartSeries(historyQuery.data) : [],
        isPending: historyQuery.isPending,
        isError: historyQuery.isError,
        refetch: () => void historyQuery.refetch(),
      };

  const waitQuery = useGetEstimatedWait(slug, {
    query: { refetchInterval: REFETCH_INTERVAL_MS },
  });
  // The heatmap is historical (a continuous aggregate) — no need to poll it.
  const heatmapQuery = useGetHospitalHeatmap(slug);
  // The live overview carries the exact per-triage integers for the breakdown
  // (and the authoritative total for the hero); poll it on the same cadence.
  // Navigating in from the overview, its query cache already holds this data, so
  // the breakdown renders immediately — no placeholder flash.
  const overviewQuery = useGetLiveOverview({
    query: { refetchInterval: REFETCH_INTERVAL_MS },
  });
  const live = overviewQuery.data?.hospitals.find((h) => h.slug === slug);

  // Collector health drives the stale-data banner (same query as the overview
  // + footer indicator; TanStack dedupes), polled on the same cadence.
  const { data: collectorStatus } = useGetCollectorStatus({
    query: { refetchInterval: REFETCH_INTERVAL_MS },
  });

  return (
    <DetailView
      slug={slug}
      live={live}
      liveIsPending={overviewQuery.isPending}
      collectorStatus={collectorStatus}
      chart={chart}
      wait={{
        data: waitQuery.data,
        isPending: waitQuery.isPending,
        isError: waitQuery.isError,
        refetch: () => void waitQuery.refetch(),
      }}
      heatmap={{
        data: heatmapQuery.data,
        isPending: heatmapQuery.isPending,
        isError: heatmapQuery.isError,
        refetch: () => void heatmapQuery.refetch(),
      }}
      zoom={zoom}
      onZoomChange={onZoomChange}
    />
  );
}
